import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export type NotificationType =
  | 'reminder' | 'expiring_item' | 'overdue_invoice'
  | 'custody_action' | 'court_date' | 'system';

export interface NotificationRow {
  id: number;
  recipient_user_id: number | null;
  notification_type: NotificationType;
  title: string;
  body: string;
  link_url: string | null;
  read_at: number | null;
  dismissed_at: number | null;
  entity_type: string | null;
  entity_id: number | null;
  created_at: number;
  updated_at: number;
}

export interface NotificationsResponse {
  notifications: NotificationRow[];
  unread_count: number;
}

export function useNotifications(unreadOnly = false) {
  const url = unreadOnly ? '/api/notifications?unread=true' : '/api/notifications';
  return useQuery({
    queryKey: ['notifications', { unreadOnly }],
    queryFn: ({ signal }) => api.get<NotificationsResponse>(url, signal),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/api/notifications/${id}/read`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/api/notifications/${id}/dismiss`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ marked_read: number }>('/api/notifications/mark-all-read', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
